import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useGetPublicOrgInvitationQuery } from '@/redux/marketerApi'

const JoinOrganization = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const {
    data: invite,
    isLoading,
    isError,
  } = useGetPublicOrgInvitationQuery(token as string, { skip: !token })

  const handleContinue = (type: 'signup' | 'login') => {
    if (token) {
      localStorage.setItem('pendingOrgInviteToken', token)
    }
    const params = new URLSearchParams()
    if (invite?.email) params.set('email', invite.email)
    if (invite?.org?.name) params.set('orgName', invite.org.name)
    navigate(`/${type === 'signup' ? 'signup' : 'signin'}?${params.toString()}`)
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#C0FD72]" />
        </div>
      )
    }

    if (isError || !invite) {
      return (
        <div className="text-center py-6">
          <p className="text-red-600 mb-4">This invitation link is invalid or has expired.</p>
          <Button
            variant="outline"
            className="bg-[#F9FAFB] border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6]"
            onClick={() => navigate('/')}
          >
            Return to Home
          </Button>
        </div>
      )
    }

    return (
      <div className="text-center space-y-8">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex items-center -space-x-4">
            <Avatar className="h-16 w-16 border-4 border-black/10">
              <AvatarImage src={invite.inviter?.avatar || undefined} />
              <AvatarFallback className="bg-[#C0FD72] text-black">
                {invite.inviter?.name?.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>
            <Avatar className="h-16 w-16 border-4 border-black/10">
              <AvatarImage src={invite.org?.logo || undefined} />
              <AvatarFallback className="bg-[#F3F4F6] text-[#0F172A]">
                {invite.org?.name?.charAt(0) || 'O'}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-[#0F172A]">You've been invited!</h2>
            <p className="text-[#4B5563] text-lg">
              <strong className="text-[#0F172A]">{invite.inviter?.name || 'A team member'}</strong>{' '}
              has invited you to join{' '}
              <strong className="text-[#3F7A0A]">
                {invite.org?.name || 'their organization'}
              </strong>{' '}
              on Athios.
            </p>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-[#E5E7EB]">
          <p className="text-sm text-[#4B5563]">
            Sign up or log in with <strong className="text-[#0F172A]">{invite.email}</strong> to
            accept the invitation.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              className="w-full bg-[#C0FD72] hover:bg-[#a8e45a] text-black font-medium"
              onClick={() => handleContinue('signup')}
            >
              Sign Up
            </Button>
            <Button
              variant="outline"
              className="w-full bg-[#F9FAFB] border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6]"
              onClick={() => handleContinue('login')}
            >
              Log In
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center py-8 relative">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]"></div>

      <div className="w-full max-w-md px-4">
        <Card className="rounded-[28px] border-0 bg-[#FFFFFF] py-4 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center pb-0 mb-6">
            <div className="flex justify-center mb-4">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardTitle className="text-3xl font-black text-[#0F172A] tracking-tight">
              athios
            </CardTitle>
          </CardHeader>
          <CardContent>{renderContent()}</CardContent>
        </Card>
      </div>
    </div>
  )
}

export default JoinOrganization
